import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import { CircularProgress, Box } from "@mui/material";

// Layouts
import AuthLayout from "@/components/common/AuthLayout";
import GlobalSnackbar from "@/components/common/GlobalSnackbar";
import GlobalPaymentDialog from "@/components/billing/GlobalPaymentDialog";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import HomePage from "@/pages/HomePage";
import ProjectListPage from "@/pages/ProjectListPage";
import IndicatorPage from "@/pages/IndicatorPage";
import DocumentsPage from "@/pages/DocumentsPage";
import WritingPage from "@/pages/WritingPage";
import PerformanceIndicatorPage from "@/pages/PerformanceIndicatorPage";
import MonitoringIndicatorPage from "@/pages/MonitoringIndicatorPage";
import DepartmentIndicatorPage from "@/pages/DepartmentIndicatorPage";
import DepartmentMonitoringIndicatorPage from "@/pages/DepartmentMonitoringIndicatorPage";
import ProjectSelfEvaluationIndicatorPage from "@/pages/ProjectSelfEvaluationIndicatorPage";
// import DepartmentSelfEvaluationIndicatorPage from "@/pages/DepartmentSelfEvaluationIndicatorPage";
import DocumentConvertPage from "@/pages/DocumentConvertPage";
import ProfilePage from "@/pages/ProfilePage";
import SettingsPage from "@/pages/SettingsPage";
import UsersPage from "@/pages/UsersPage";
import AdminTasksPage from "@/pages/AdminTasksPage";
import SubscriptionRecordsAdmin from "@/components/admin/SubscriptionRecordsAdmin";
import WorkspacePage from "@/pages/project-workspace";
import KnowledgeBasePage from "@/pages/KnowledgeBasePage";
import KnowledgeBaseDetailPage from "@/pages/KnowledgeBaseDetailPage";

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Public Route Component
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return !isAuthenticated ? <>{children}</> : <Navigate to="/" />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <AuthLayout>
              <RegisterPage />
            </AuthLayout>
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      {/* Redirect /projects route to home page, keep project-specific routes */}
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <Navigate to="/" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute>
            <WorkspacePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/indicators"
        element={
          <ProtectedRoute>
            <IndicatorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/performance-indicators"
        element={
          <ProtectedRoute>
            <PerformanceIndicatorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/monitoring-indicators"
        element={
          <ProtectedRoute>
            <MonitoringIndicatorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/department-indicators"
        element={
          <ProtectedRoute>
            <DepartmentIndicatorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/department-monitoring"
        element={
          <ProtectedRoute>
            <DepartmentMonitoringIndicatorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/project-self-evaluation-indicators"
        element={
          <ProtectedRoute>
            <ProjectSelfEvaluationIndicatorPage />
          </ProtectedRoute>
        }
      />
      {/* 暂时注释，先完成项目自评 */}
      {/* <Route
        path="/projects/:projectId/department-self-evaluation-indicators"
        element={
          <ProtectedRoute>
            <DepartmentSelfEvaluationIndicatorPage />
          </ProtectedRoute>
        }
      /> */}
      <Route
        path="/projects/:projectId/documents"
        element={
          <ProtectedRoute>
            <DocumentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/writing"
        element={
          <ProtectedRoute>
            <WritingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/document-convert"
        element={
          <ProtectedRoute>
            <DocumentConvertPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/list"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tasks"
        element={
          <ProtectedRoute>
            <AdminTasksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/subscription-records"
        element={
          <ProtectedRoute>
            <SubscriptionRecordsAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-base"
        element={
          <ProtectedRoute>
            <KnowledgeBasePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-base/:knowledgeBaseId"
        element={
          <ProtectedRoute>
            <KnowledgeBaseDetailPage />
          </ProtectedRoute>
        }
      />

      {/* 404 Route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <WebSocketProvider>
        <AppRoutes />
        <GlobalSnackbar />
        <GlobalPaymentDialog />
      </WebSocketProvider>
    </AuthProvider>
  );
};

export default App;
